import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { RefreshCw, MessageCircle, CheckCheck, AlertCircle, Trash2, ArrowLeft, Phone, Clock, FileJson } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppLogsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, messages: 0, status_updates: 0, with_errors: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, message, status
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const eventType = filter === "all" ? "" : `&event_type=${filter}`;
      const response = await fetch(
        `${BACKEND_URL}/api/whatsapp/webhook-logs?limit=100${eventType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Fehler beim Laden der Logs");
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(fetchLogs, 10000);
    return () => clearInterval(interval);
  }, [fetchLogs]);

  const clearOldLogs = async () => {
    if (!confirm("Logs älter als 7 Tage löschen?")) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/whatsapp/webhook-logs?older_than_days=7`,
        { 
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.deleted_count} alte Logs gelöscht`);
        fetchLogs();
      }
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const date = new Date(ts);
    return date.toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const getEventIcon = (log) => {
    if (log.processing?.is_message) {
      return <MessageCircle className="h-4 w-4 text-green-600" />;
    }
    if (log.processing?.is_status_update) {
      return <CheckCheck className="h-4 w-4 text-blue-500" />;
    }
    return <AlertCircle className="h-4 w-4 text-gray-400" />;
  };

  const getEventBadge = (log) => {
    const eventType = log.parsed?.event_type || "unknown";
    
    if (eventType.startsWith("message_")) {
      return <Badge className="bg-green-100 text-green-800">{eventType.replace("message_", "")}</Badge>;
    }
    if (eventType.startsWith("status_")) {
      const status = eventType.replace("status_", "");
      const colors = {
        sent: "bg-gray-100 text-gray-800",
        delivered: "bg-blue-100 text-blue-800",
        read: "bg-purple-100 text-purple-800",
        failed: "bg-red-100 text-red-800"
      };
      return <Badge className={colors[status] || "bg-gray-100 text-gray-800"}>{status}</Badge>;
    }
    return <Badge variant="outline">{eventType}</Badge>;
  };

  const getProcessingStatus = (log) => {
    if (log.processing?.errors?.length > 0) {
      return <Badge variant="destructive">Fehler</Badge>;
    }
    if (log.processing?.saved_to_db) {
      return <Badge className="bg-green-100 text-green-800">Gespeichert</Badge>;
    }
    if (log.processing?.is_status_update) {
      return <Badge variant="secondary">Übersprungen</Badge>;
    }
    return <Badge variant="outline">-</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate("/whatsapp")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Webhook Logs</h1>
            <p className="text-sm text-gray-500">
              Live-Übersicht aller eingehenden Meta-Webhooks
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
          <Button variant="outline" size="sm" onClick={clearOldLogs} className="text-red-600">
            <Trash2 className="h-4 w-4 mr-2" />
            Alte löschen
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-gray-500">Gesamt</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{stats.messages}</div>
            <div className="text-sm text-gray-500">Nachrichten</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{stats.status_updates}</div>
            <div className="text-sm text-gray-500">Status-Updates</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-red-600">{stats.with_errors}</div>
            <div className="text-sm text-gray-500">Mit Fehlern</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
        >
          Alle
        </Button>
        <Button
          variant={filter === "message" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("message")}
        >
          <MessageCircle className="h-4 w-4 mr-2" />
          Nur Nachrichten
        </Button>
        <Button
          variant={filter === "status" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("status")}
        >
          <CheckCheck className="h-4 w-4 mr-2" />
          Nur Status
        </Button>
      </div>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Webhook-Verlauf</CardTitle>
          <CardDescription>
            Alle eingehenden Webhooks von Meta (automatische Aktualisierung alle 10s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b">
                  <th className="text-left p-2 text-sm font-medium">Zeit</th>
                  <th className="text-left p-2 text-sm font-medium">Typ</th>
                  <th className="text-left p-2 text-sm font-medium">Telefon</th>
                  <th className="text-left p-2 text-sm font-medium">Inhalt</th>
                  <th className="text-left p-2 text-sm font-medium">Status</th>
                  <th className="text-left p-2 text-sm font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center p-8 text-gray-500">
                      {loading ? "Lade..." : "Keine Logs vorhanden"}
                    </td>
                  </tr>
                ) : (
                  logs.map((log, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3 text-gray-400" />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          {getEventIcon(log)}
                          {getEventBadge(log)}
                        </div>
                      </td>
                      <td className="p-2 text-sm">
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-gray-400" />
                          {log.parsed?.from_phone || "-"}
                        </div>
                      </td>
                      <td className="p-2 text-sm max-w-[200px] truncate">
                        {log.parsed?.message_body || log.parsed?.status || "-"}
                      </td>
                      <td className="p-2">
                        {getProcessingStatus(log)}
                      </td>
                      <td className="p-2">
                        <Dialog open={detailOpen && selectedLog === log} onOpenChange={(open) => {
                          setDetailOpen(open);
                          if (!open) setSelectedLog(null);
                        }}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedLog(log);
                                setDetailOpen(true);
                              }}
                            >
                              <FileJson className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Webhook Details</DialogTitle>
                              <DialogDescription>
                                {formatTimestamp(log?.timestamp)}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-semibold mb-2">Geparste Daten</h4>
                                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(log?.parsed, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <h4 className="font-semibold mb-2">Verarbeitung</h4>
                                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(log?.processing, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <h4 className="font-semibold mb-2">Headers</h4>
                                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(log?.headers, null, 2)}
                                </pre>
                              </div>
                              {log?.processing?.errors?.length > 0 && (
                                <div>
                                  <h4 className="font-semibold mb-2 text-red-600">Fehler</h4>
                                  <pre className="bg-red-50 p-3 rounded text-xs overflow-x-auto text-red-800">
                                    {JSON.stringify(log?.processing?.errors, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900">Wie funktioniert das Logging?</h4>
              <ul className="text-sm text-blue-800 mt-1 space-y-1">
                <li><strong>💬 Nachrichten:</strong> Eingehende WhatsApp-Nachrichten von Kunden</li>
                <li><strong>📊 Status-Updates:</strong> Zustellbestätigungen (sent, delivered, read)</li>
                <li><strong>✅ Gespeichert:</strong> Nachricht wurde in der Datenbank gespeichert</li>
                <li><strong>⏭️ Übersprungen:</strong> Status-Update (wird nicht als Nachricht gespeichert)</li>
                <li><strong>❌ Fehler:</strong> Problem bei der Verarbeitung (Details in JSON-Ansicht)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
